/**
 * Partnr Canvas Action Executor — Phase 3
 * Receives parsed canvas action payloads from the Partnr stream and executes them locally.
 */

import { useCanvasStore, type ItemType } from '@/stores/canvasStore';
import { useArrowStore } from '@/stores/arrowStore';
import {
  computeGridLayout,
  computeHorizontalLayout,
  computeVerticalLayout,
  computeRadialLayout,
  computeClusterLayout,
} from './layoutAlgorithms';
import { applyLayout, getDefaultWidth, getDefaultHeight } from './canvasActions';
import { getViewportCenter } from './viewportHelpers';
import { computeDagreLayout, getDagreDirection, getDagreConnectors } from './dagreLayout';

interface CanvasAction {
  action: string;
  payload: Record<string, unknown>;
  confirmation?: string | null;
}

/**
 * Execute a canvas action received from the Partnr stream.
 */
export async function executeCanvasAction(action: CanvasAction): Promise<void> {
  console.log('[CanvasAction] Executing:', action.action, action.payload);

  switch (action.action) {
    case 'create_canvas_items':
      await handleCreateItems(action.payload);
      break;
    case 'arrange_layout':
      await handleArrangeLayout(action.payload);
      break;
    case 'create_connections':
      await handleCreateConnections(action.payload);
      break;
    case 'create_diagram':
      await handleCreateDiagram(action.payload);
      break;
    case 'focus_viewport':
      await handleFocusViewport(action.payload);
      break;
  }
}

/**
 * Parse a stream chunk for canvas action delimiters.
 * Returns { canvasAction, remainingText } if found, null otherwise.
 */
export function parseCanvasAction(
  text: string
): { canvasAction: CanvasAction; remainingText: string } | null {
  const match = text.match(/<!--CANVAS_ACTION:([\s\S]*?)-->/);
  if (!match) return null;

  try {
    const canvasAction = JSON.parse(match[1]) as CanvasAction;
    const remainingText = text.replace(/<!--CANVAS_ACTION:[\s\S]*?-->/, '').trim();
    return { canvasAction, remainingText };
  } catch (e) {
    console.error('[CanvasAction] Failed to parse:', e);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Action Handlers
// ---------------------------------------------------------------------------

async function handleCreateItems(payload: Record<string, unknown>): Promise<void> {
  const store = useCanvasStore.getState();
  const items = payload.items as Array<{
    item_type: string;
    title: string;
    body: string;
    color?: string;
  }>;

  if (!Array.isArray(items) || items.length === 0) {
    console.error('[CanvasAction] handleCreateItems: items is empty or invalid', payload);
    return;
  }

  const layout = (payload.layout as string) || 'grid';
  const { panX, panY, zoom } = store;
  const viewportCenter = getViewportCenter({ panX, panY, zoom });

  const sized = items.map((item, i) => ({
    id: `ghost-${i}`,
    width: getDefaultWidth(item.item_type),
    height: getDefaultHeight(item.item_type),
  }));

  const positions = computeLayoutPositions(sized, layout, viewportCenter.x, viewportCenter.y);

  // Phase 4: Create ghost placeholders instantly
  const ghostIds: string[] = [];
  for (let i = 0; i < items.length; i++) {
    const pos = positions.get(`ghost-${i}`);
    if (!pos) continue;
    const ghostId = `ghost-create-${Date.now()}-${i}`;
    ghostIds.push(ghostId);
    store.createGhost({
      id: ghostId,
      itemType: items[i].item_type as ItemType,
      x: pos.x,
      y: pos.y,
      width: getDefaultWidth(items[i].item_type),
      height: getDefaultHeight(items[i].item_type),
      ghostColor: items[i].color,
    });
  }

  // Focus on ghosts immediately so the user sees them appear
  if (ghostIds.length > 0) {
    await handleFocusViewport({ itemIds: ghostIds });
  }

  // Hydrate ghosts one by one with stagger delay
  const createdIds: string[] = [];
  for (let i = 0; i < items.length; i++) {
    const ghostId = ghostIds[i];
    if (!ghostId) continue;

    // Update progress as we go
    useCanvasStore.getState().updateGhostProgress(ghostId, (i + 0.5) / items.length);

    const item = items[i];
    const pos = positions.get(`ghost-${i}`);
    if (!pos) continue;

    const created = await useCanvasStore.getState().createItem({
      item_type: item.item_type as ItemType,
      content: buildContentJsonb(item),
      position_x: pos.x,
      position_y: pos.y,
      width: getDefaultWidth(item.item_type),
      height: getDefaultHeight(item.item_type),
    });

    if (created) {
      createdIds.push(created.id);
      // Hydrate: replace ghost with real item
      useCanvasStore.getState().hydrateGhost(ghostId, {
        id: created.id,
        content: created.content as Record<string, unknown>,
      });
    } else {
      // Remove ghost if creation failed
      useCanvasStore.getState().removeGhost(ghostId);
    }

    // Stagger delay between items for visual effect
    if (i < items.length - 1) {
      await delay(150);
    }
  }

  // Re-focus on the created (now real) items
  if (createdIds.length > 0) {
    await handleFocusViewport({ itemIds: createdIds });
  }
}

async function handleArrangeLayout(payload: Record<string, unknown>): Promise<void> {
  const store = useCanvasStore.getState();
  const itemIds = (payload.resolvedItemIds as string[] | null) || store.items.map(i => i.id);
  const layoutType = (payload.layout_type as string) || 'grid';

  const items = itemIds
    .map(id => store.items.find(i => i.id === id))
    .filter(Boolean)
    .map(item => ({
      id: item!.id,
      width: item!.width ?? 200,
      height: item!.height ?? 150,
    }));

  if (items.length === 0) return;

  const { panX, panY, zoom } = store;
  const viewportCenter = getViewportCenter({ panX, panY, zoom });

  // Smart layout: if items have existing connections, use dagre for topology-aware layout
  const itemIdSet = new Set(items.map(i => i.id));
  const arrowState = useArrowStore.getState();
  const relevantEdges = arrowState.arrows
    .filter(a => a.start_item_id && a.end_item_id && itemIdSet.has(a.start_item_id) && itemIdSet.has(a.end_item_id))
    .map(a => ({ source: a.start_item_id!, target: a.end_item_id! }));

  let positions: Map<string, { x: number; y: number }>;

  if (relevantEdges.length > 0 && layoutType !== 'cluster' && layoutType !== 'radial') {
    // Use dagre for connected items
    const direction = layoutType === 'horizontal_row' ? 'LR' as const : 'TB' as const;
    positions = computeDagreLayout(items, relevantEdges, {
      direction,
      centerX: viewportCenter.x,
      centerY: viewportCenter.y,
    });
  } else {
    positions = computeLayoutPositions(items, layoutType, viewportCenter.x, viewportCenter.y);
  }

  await applyLayout(positions, { animate: true });
}

async function handleCreateConnections(payload: Record<string, unknown>): Promise<void> {
  const connections = payload.connections as Array<{
    sourceItemId: string;
    targetItemId: string;
    label?: string;
    connection_type?: string;
    style?: string;
  }>;

  const arrowStore = useArrowStore.getState();
  const boardId = useCanvasStore.getState().currentBoardId;
  if (!boardId) return;

  for (const conn of connections) {
    if (conn.sourceItemId && conn.targetItemId) {
      await arrowStore.createArrow({
        board_id: boardId,
        start_item_id: conn.sourceItemId,
        end_item_id: conn.targetItemId,
        start_connector: 'right',
        end_connector: 'left',
        label: conn.label,
        connection_type: (conn.connection_type as 'default' | 'causes' | 'blocks' | 'contains' | 'sequence' | 'related') || 'default',
        style: (conn.style as 'bezier' | 'straight' | 'step') || 'bezier',
        stroke_color: getConnectionTypeColor(conn.connection_type),
        stroke_width: 2,
        animated: false,
        start_arrowhead: false,
        end_arrowhead: true,
        created_by: 'ai',
      });
    }
  }
}

async function handleCreateDiagram(payload: Record<string, unknown>): Promise<void> {
  const store = useCanvasStore.getState();
  const { panX, panY, zoom } = store;
  const viewportCenter = getViewportCenter({ panX, panY, zoom });

  if (!Array.isArray(payload.nodes) || (payload.nodes as unknown[]).length === 0) {
    console.error('[CanvasAction] handleCreateDiagram: nodes is empty or invalid', payload);
    return;
  }

  const nodes = payload.nodes as Array<{
    id: string;
    item_type: string;
    title: string;
    body?: string;
    color?: string;
    role?: string;
  }>;
  const edges = payload.edges as Array<{
    source_id: string;
    target_id: string;
    label?: string;
    style?: string;
  }>;
  const diagramType = (payload.diagram_type as string) || 'flowchart';

  const sized = nodes.map(node => ({
    id: node.id,
    width: getDefaultWidth(node.item_type),
    height: getDefaultHeight(node.item_type),
  }));

  // Use dagre for topology-aware layout when edges exist, fallback to simple layouts
  let positions: Map<string, { x: number; y: number }>;
  const dagreDirection = getDagreDirection(diagramType);

  if (edges.length > 0 && diagramType !== 'comparison') {
    positions = computeDagreLayout(
      sized,
      edges.map(e => ({ source: e.source_id, target: e.target_id })),
      {
        direction: dagreDirection,
        centerX: viewportCenter.x,
        centerY: viewportCenter.y,
      }
    );
  } else {
    // Fallback for diagrams without edges or comparison type
    const layoutMap: Record<string, string> = {
      flowchart: 'vertical_column',
      mind_map: 'radial',
      process: 'horizontal_row',
      hierarchy: 'vertical_column',
      comparison: 'grid',
    };
    const layoutType = layoutMap[diagramType] || 'grid';
    positions = computeLayoutPositions(sized, layoutType, viewportCenter.x, viewportCenter.y);
  }

  // Phase 4: Create ghost placeholders for all diagram nodes instantly
  const ghostIdMap = new Map<string, string>(); // node.id -> ghostId
  const ghostIds: string[] = [];
  for (const node of nodes) {
    const pos = positions.get(node.id);
    if (!pos) continue;
    const ghostId = `ghost-diagram-${Date.now()}-${node.id}`;
    ghostIdMap.set(node.id, ghostId);
    ghostIds.push(ghostId);
    store.createGhost({
      id: ghostId,
      itemType: node.item_type as ItemType,
      x: pos.x,
      y: pos.y,
      width: getDefaultWidth(node.item_type),
      height: getDefaultHeight(node.item_type),
      ghostColor: node.color || getRoleColor(node.role),
    });
  }

  // Focus on ghosts immediately
  if (ghostIds.length > 0) {
    await handleFocusViewport({ itemIds: ghostIds });
  }

  // Hydrate ghosts one by one with stagger delay
  const idMap = new Map<string, string>(); // node temp id -> real id
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    const ghostId = ghostIdMap.get(node.id);
    if (!ghostId) continue;

    // Update progress
    useCanvasStore.getState().updateGhostProgress(ghostId, (i + 0.5) / nodes.length);

    const pos = positions.get(node.id);
    if (!pos) continue;

    const created = await useCanvasStore.getState().createItem({
      item_type: node.item_type as ItemType,
      content: buildContentJsonb({
        ...node,
        color: node.color || getRoleColor(node.role),
      }),
      position_x: pos.x,
      position_y: pos.y,
      width: getDefaultWidth(node.item_type),
      height: getDefaultHeight(node.item_type),
    });

    if (created) {
      idMap.set(node.id, created.id);
      useCanvasStore.getState().hydrateGhost(ghostId, {
        id: created.id,
        content: created.content as Record<string, unknown>,
      });
    } else {
      useCanvasStore.getState().removeGhost(ghostId);
    }

    // Stagger delay
    if (i < nodes.length - 1) {
      await delay(150);
    }
  }

  // Create connections after all nodes are hydrated
  const arrowStore = useArrowStore.getState();
  const boardId = useCanvasStore.getState().currentBoardId;
  if (!boardId) return;

  // Use direction-aware connectors from dagre layout
  const connectors = getDagreConnectors(dagreDirection);

  for (const edge of edges) {
    const sourceId = idMap.get(edge.source_id);
    const targetId = idMap.get(edge.target_id);

    if (sourceId && targetId) {
      await arrowStore.createArrow({
        board_id: boardId,
        start_item_id: sourceId,
        end_item_id: targetId,
        start_connector: connectors.startConnector,
        end_connector: connectors.endConnector,
        label: edge.label,
        style: (edge.style as 'bezier' | 'straight' | 'step') || getDefaultEdgeStyle(diagramType),
        connection_type: 'sequence',
        stroke_color: '#94a3b8',
        stroke_width: 2,
        animated: true,
        start_arrowhead: false,
        end_arrowhead: true,
        created_by: 'ai',
      });
    }
  }

  // Focus on the diagram
  const createdIds = Array.from(idMap.values());
  if (createdIds.length > 0) {
    await handleFocusViewport({ itemIds: createdIds });
  }

  // Settle connection animations after a delay
  setTimeout(() => {
    const arrows = useArrowStore.getState().arrows;
    arrows
      .filter(a => a.created_by === 'ai' && a.animated)
      .forEach(a => useArrowStore.getState().updateArrow(a.id, { animated: false }));
  }, 1500);
}

async function handleFocusViewport(payload: Record<string, unknown>): Promise<void> {
  const store = useCanvasStore.getState();
  const itemIds = payload.itemIds as string[];

  const items = itemIds
    .map(id => store.items.find(i => i.id === id))
    .filter(Boolean);

  if (items.length === 0) return;

  // Compute bounding box
  const padding = 80;
  const bounds = {
    minX: Math.min(...items.map(i => i!.position_x)) - padding,
    maxX: Math.max(...items.map(i => i!.position_x + i!.width)) + padding,
    minY: Math.min(...items.map(i => i!.position_y)) - padding,
    maxY: Math.max(...items.map(i => i!.position_y + i!.height)) + padding,
  };

  const boundsWidth = bounds.maxX - bounds.minX;
  const boundsHeight = bounds.maxY - bounds.minY;
  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;

  const zoomToFit = (payload.zoomLevel as number) ?? Math.min(
    windowWidth / boundsWidth,
    windowHeight / boundsHeight,
    1.0
  );

  const centerX = (bounds.minX + bounds.maxX) / 2;
  const centerY = (bounds.minY + bounds.maxY) / 2;

  // Update viewport directly (the store handles pan/zoom)
  useCanvasStore.setState({
    panX: -centerX * zoomToFit + windowWidth / 2,
    panY: -centerY * zoomToFit + windowHeight / 2,
    zoom: zoomToFit,
  });
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function computeLayoutPositions(
  items: Array<{ id: string; width: number; height: number }>,
  layoutType: string,
  centerX: number,
  centerY: number
): Map<string, { x: number; y: number }> {
  switch (layoutType) {
    case 'grid':
      return computeGridLayout(items, { startX: centerX - 300, startY: centerY - 200 });
    case 'horizontal_row':
      return computeHorizontalLayout(items, { startX: centerX - 400, startY: centerY });
    case 'vertical_column':
      return computeVerticalLayout(items, { startX: centerX, startY: centerY - 300 });
    case 'radial':
      if (items.length > 1) {
        return computeRadialLayout(items[0], items.slice(1), { centerX, centerY });
      }
      return computeGridLayout(items, { startX: centerX, startY: centerY });
    case 'cluster':
      return computeClusterLayout(items, { centerX, centerY });
    default:
      return computeGridLayout(items, { startX: centerX, startY: centerY });
  }
}

function buildContentJsonb(item: {
  item_type?: string;
  title: string;
  body?: string;
  color?: string;
}): Record<string, unknown> {
  const type = item.item_type || 'sticky';

  if (type === 'sticky') {
    return {
      text: item.body || item.title,
      color: item.color || '#FEF3C7',
    };
  }

  if (type === 'text_card') {
    return {
      title: item.title,
      body: item.body || '',
    };
  }

  if (type === 'document') {
    return {
      title: item.title,
      body: `<p>${(item.body || '').replace(/\n/g, '</p><p>')}</p>`,
    };
  }

  return { text: item.body || item.title };
}

function getConnectionTypeColor(type?: string): string {
  const colorMap: Record<string, string> = {
    default: '#94a3b8',
    causes: '#f97316',
    blocks: '#ef4444',
    contains: '#8b5cf6',
    sequence: '#3b82f6',
    related: '#6b7280',
  };
  return colorMap[type || 'default'] || '#94a3b8';
}

function getRoleColor(role?: string): string {
  const roleColors: Record<string, string> = {
    start: '#dcfce7',
    end: '#fee2e2',
    decision: '#fef3c7',
    process: '#dbeafe',
    input: '#ede9fe',
    output: '#cffafe',
    default: '#FEF3C7',
  };
  return roleColors[role || 'default'] || '#FEF3C7';
}

function getDefaultEdgeStyle(diagramType: string): 'bezier' | 'straight' | 'step' {
  switch (diagramType) {
    case 'flowchart': return 'step';
    case 'process': return 'bezier';
    case 'hierarchy': return 'step';
    case 'mind_map': return 'bezier';
    case 'comparison': return 'straight';
    default: return 'bezier';
  }
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}
